import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GetRecordingResponse, PostRecordingData, PostRecordingResponse, RecordingBlobPayload, RecordingState } from "./index.types";

export const initialRecordingStates: RecordingState = {
  recordingIsLoading: false,
  recordingSuccess: null,
  recordingPostSuccess: null,
  recPosted: null,
  processingStatus: null,
  recordingPostLoading: false,
  recordingData: [],
  isRecordingAvaiable: false,
  encounterRecordingId: null,
  dictation: "",
  recordingBlob: null,
  recordingTimestamps: [],
};

const name = "recording";

export const recordingSlice = createSlice({
  name,
  initialState: initialRecordingStates,
  reducers: {
    getRecording: (state) => {
      state.recordingIsLoading = true;
    },
    getRecordingSuccess: (state, { payload }: PayloadAction<GetRecordingResponse>) => {
      state.recordingIsLoading = false;
      state.processingStatus = payload?.processing_status || null;
      state.recordingSuccess = true;
      state.recordingData = payload?.end_note_data || [];
    },
    getRecordingFail: (state) => {
      state.recordingIsLoading = false;
      state.recordingSuccess = false;
      state.recordingData = [];
    },
    updateUploadingStatus: (state) => {
      state.isRecordingAvaiable = true;
    },
    //post recording data
    postRecording: (state) => {
      state.recordingPostLoading = true;
    },
    postRecordingSuccess: (state, action: PayloadAction<PostRecordingResponse | PostRecordingResponse[]>) => {
      state.recPosted = "posted";
      state.recordingPostLoading = false;
      state.recordingPostSuccess = true;
      const responseData = Array.isArray(action?.payload) ? action?.payload?.[0] : action?.payload;
      state.recordingData = responseData?.end_note_data || [];
      state.encounterRecordingId = responseData?.id || null;
      state.processingStatus = responseData?.processing_status || "PROCESSING";
      state.recordingBlob = null;
      state.recordingTimestamps = [];
    },
    postRecordingFail: (state) => {
      state.recPosted = "posted";
      state.recordingPostLoading = false;
      state.recordingPostSuccess = false;
      state.recordingData = [];
    },
    saveDictation: (state, { payload }: PayloadAction<string>) => {
      state.dictation = state.dictation + " " + payload;
    },
    saveRecordingBlob: (state, { payload }: PayloadAction<RecordingBlobPayload>) => {
      state.recordingBlob = payload.blob;
      state.recordingTimestamps = payload.timestamps;
    },
    clearRecordingBlob: (state) => {
      state.recordingBlob = null;
      state.recordingTimestamps = [];
    },
    clearRecordingData: (state) => {
      state.recordingIsLoading = false;
      state.encounterRecordingId = null;
      state.isRecordingAvaiable = false;
      state.recordingSuccess = null;
      state.dictation = "";
      state.recordingPostSuccess = null;
      state.recordingPostLoading = false;
      state.recordingData = [];
      state.recordingBlob = null;
      state.recordingTimestamps = [];
    },
    resetProcessingStatus: (state) => {
      state.processingStatus = null;
    },
    extraReducers: (builder: any) => {
      builder.addCase(
        "postRecordingSuccess",
        (state: RecordingState, action: PayloadAction<PostRecordingResponse | PostRecordingResponse[]>) => {
          state.recordingPostSuccess = true;
          const responseData = Array.isArray(action.payload) ? action.payload[0] : action.payload;
          state.recordingData = responseData?.end_note_data || [];
          state.recordingPostLoading = false;
          // Dispatch getRecording action after postRecordingSuccess
          return { ...state, shouldFetchRecording: true } as any;
        }
      );
    },
  },
});

export const {
  getRecording,
  updateUploadingStatus,
  saveDictation,
  saveRecordingBlob,
  clearRecordingBlob,
  clearRecordingData,
  resetProcessingStatus,
} = recordingSlice.actions;

export default recordingSlice.reducer;

export const getRecordingData = (encRecordingId: string | number) => ({
  payload: {
    apiName: name,
    method: "get",
    url: `/api/encounter-recordings/${encRecordingId}/`,
  },
  type: "getRecording",
});

export const postRecordingData = (data: PostRecordingData) => ({
  payload: {
    apiName: name,
    method: "post",
    data,
    formData: true,
    url: `/api/encounter-recordings/`,
  },
  type: "postRecording",
});

export const reprocessRecording = (encRecordingId: string | number) => ({
  payload: {
    apiName: name,
    method: "patch",
    url: `/api/encounter-recordings/${encRecordingId}/reprocess/`,
  },
  type: "postRecording",
});
